import React, { useEffect, useRef, useState } from 'react';
import './VideoPage.css';
import { shouldRenderVideoEmbed } from '../../config/videoEmbeds';

/**
 * A standalone page for displaying a Vimeo video with custom controls and logo overlay.
 * This page is not linked from the main navigation.
 * Exports:
 * - default: React function component; renders an empty shell when the video page embed is disabled in config.
 */
function VideoPage() {
  const embedActive = shouldRenderVideoEmbed('videoPage');
  const playerRef = useRef(null);
  const [player, setPlayer] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(() => {
    // Start muted on mobile devices (screen width <= 768px)
    return window.innerWidth <= 768;
  });
  const [volume, setVolume] = useState(1);
  const [duration, setDuration] = useState(0);
  const [currentTime, setCurrentTime] = useState(0);

  useEffect(() => {
    if (!embedActive) {
      return;
    }
    // Load Vimeo API and initialize player
    const loadVimeoAPI = () => {
      if (!window.Vimeo) {
        const script = document.createElement('script');
        script.src = 'https://player.vimeo.com/api/player.js';
        script.onload = initializePlayer;
        document.head.appendChild(script);
      } else {
        initializePlayer();
      }
    };

    const initializePlayer = () => {
      if (window.Vimeo && playerRef.current) {
        const vimeoPlayer = new window.Vimeo.Player(playerRef.current, {
          id: 1145764010,
          width: window.innerWidth,
          height: window.innerHeight,
          autoplay: false,
          controls: false,
          muted: window.innerWidth <= 768 // Start muted on mobile
        });

        vimeoPlayer.ready().then(() => {
          setPlayer(vimeoPlayer);

          // Ensure mute state is synchronized on mobile
          if (window.innerWidth <= 768) {
            vimeoPlayer.setMuted(true);
            setIsMuted(true);
            setVolume(0);
          }

          // Get initial duration
          vimeoPlayer.getDuration().then((dur) => {
            console.log('Video duration:', dur);
            setDuration(dur);
          }).catch((error) => {
            console.error('Error getting duration:', error);
          });

          // Listen for events to update our UI
          vimeoPlayer.on('play', () => setIsPlaying(true));
          vimeoPlayer.on('pause', () => setIsPlaying(false));
          vimeoPlayer.on('timeupdate', (data) => {
            setCurrentTime(data.seconds);
          });
          vimeoPlayer.on('volumechange', async (data) => {
            setVolume(data.volume);
            // Check actual mute state from Vimeo player
            try {
              const muted = await vimeoPlayer.getMuted();
              setIsMuted(muted);
              console.log('Volume change - volume:', data.volume, 'muted:', muted);
            } catch (error) {
              // Fallback: assume muted if volume is 0
              setIsMuted(data.volume === 0);
            }
          });

          vimeoPlayer.on('loaded', () => {
            console.log('Video loaded');
          });
        }).catch((error) => {
          console.error('Vimeo player error:', error);
        });
      }
    };

    loadVimeoAPI();

    return () => {
      if (player) {
        player.destroy();
      }
    };
  }, [embedActive, player]);

  const togglePlay = async () => {
    if (player) {
      if (isPlaying) {
        await player.pause();
      } else {
        await player.play();
      }
    }
  };

  const toggleMute = async () => {
    if (player) {
      try {
        // Just trigger the mute/unmute action - let the volumechange event handle state updates
        if (isMuted) {
          await player.setVolume(1);
        } else {
          await player.setVolume(0);
        }
      } catch (error) {
        console.error('Mute toggle error:', error);
      }
    }
  };

  const handleVolumeChange = async (e) => {
    const newVolume = parseFloat(e.target.value);
    if (player) {
      await player.setVolume(newVolume);
      setVolume(newVolume);
      setIsMuted(newVolume === 0);
    }
  };

  const handleSeek = async (e) => {
    if (player && duration) {
      // Always use the progress-container bounds, not the child element
      const progressContainer = e.currentTarget;
      const rect = progressContainer.getBoundingClientRect();
      const clickX = e.clientX - rect.left;

      // Ensure clickX is within bounds
      const clampedClickX = Math.max(0, Math.min(clickX, rect.width));
      const percentage = clampedClickX / rect.width;
      const seekTime = percentage * duration;

      console.log(`Seeking to ${seekTime}s (${percentage * 100}%)`);

      try {
        await player.setCurrentTime(seekTime);
        // Update local state immediately for responsive UI
        setCurrentTime(seekTime);
      } catch (error) {
        console.error('Seek error:', error);
      }
    }
  };

  const toggleFullscreen = () => {
    const container = document.querySelector('.video-container');
    if (!document.fullscreenElement) {
      container.requestFullscreen();
    } else {
      document.exitFullscreen();
    }
  };

  const formatTime = (time) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  if (!embedActive) {
    return <div className="video-page" />;
  }

  return (
    <div className="video-page">
      <div className="video-container">
        <div ref={playerRef} className="vimeo-player"></div>
          <div className="custom-controls">
            <button className="control-btn play-btn" onClick={togglePlay}>
              <ion-icon name={isPlaying ? "pause" : "play"} size="large"></ion-icon>
            </button>

          <div className="progress-container" onClick={handleSeek}>
            <div
              className="progress-bar"
              style={{ width: `${(currentTime / duration) * 100}%` }}
            ></div>
          </div>

          <div className="time-display">
            {formatTime(currentTime)}/{formatTime(duration)}
          </div>

            <div className="volume-container">
              <button className="control-btn volume-btn" onClick={toggleMute}>
                <ion-icon name={isMuted ? "volume-mute" : "volume-high"}></ion-icon>
              </button>
            <input
              type="range"
              min="0"
              max="1"
              step="0.1"
              value={volume}
              onChange={handleVolumeChange}
              className="volume-slider"
            />
          </div>

            <button className="control-btn fullscreen-btn" onClick={toggleFullscreen}>
              <ion-icon name="scan"></ion-icon>
            </button>
        </div>
      </div>
    </div>
  );
}

export default VideoPage;